import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { flushSync } from "react-dom";
import { Link } from "react-router-dom";
import { Modal, Tooltip, OverlayTrigger } from "react-bootstrap";
import CollapseHeader from "../../../core/common/collapse-header";
import Table from "../../../core/common/dataTable/index";

import { PipelineService, Pipeline, PipelineRequest } from "./pipeline.service";
import PipelineForm from "./pipelineForm";
import { PipelineFormDataProvider } from "./pipelineFormDataContext";
import { useDispatch, useSelector } from "react-redux";
import { all_routes } from "../../router/all_routes";
import { usePipelines } from "./usePipelines";
import ManageColumns from "../../../components/filters/ManageColumns";
import {
  showConfirmationAlert,
  showSuccessAlert,
  showErrorAlert,
} from "../../uiInterface/base-ui/alertservice";
import {
  getLifecycleTypePluralName,
  getLifecycleTypeName,
} from "../../../utils/lifecycleUtils";
import { AuthContext } from "../../../context/authcontext";
import male from "../../../../src/assets/male.png";
import TableFilters, {
  FilterState,
  FilterConfig,
  mergeTableFilterChanges,
} from "../../../components/filters/TableFilters";
import SortableHeader from "../../../components/filters/SortableHeader";
import SortByDropdown from "../../../components/filters/SortByDropdown";
import ListFilterChips, {
  getClearedListFilters,
} from "../components/ListFilterChips";
import TablePagination from "../../../components/pagination/TablePagination";
import { useTenantChange } from "../../../hooks/useTenantChange";
import { getStageColor } from "../../../utils/stageColors";
import { sortRecords } from "../../../utils/sortUtils";
import ExportComponent from "../../../components/ExportComponent";
import { formatCustomDateTime } from "../../../utils/dateUtils";
import LifecycleFilterDropdown from "../components/LifecycleFilterDropdown";
import EntityDetailsModal, {
  EntityDetailSection,
  EntityDetailLink,
  PriorityBadge,
} from "../../../components/EntityDetailsModal/EntityDetailsModal";
import { openOffcanvas } from "../../../utils/offcanvasUtils";
import { getApiErrorMessage } from "../../../utils/apiErrorUtils";

interface PipelineListProps {
  onViewSwitch?: (view: "list" | "kanban") => void;
}

const PipelineList = ({ onViewSwitch }: PipelineListProps) => {
  const route = all_routes;
  const dispatch = useDispatch();
  const { selectedTenantId } = useContext(AuthContext);
  const {
    pipelines,
    loading,
    backgroundLoading,
    error,
    pagination,
    addPipeline,
    updatePipeline,
    removePipeline,
    fetchPipelines,
    isInitialized,
    clearBackgroundLoading,
    clearPipelinesData,
  } = usePipelines();
  const [data, setData] = useState<Pipeline[]>([]);
  const [tenantLoading, setTenantLoading] = useState(false);

  const [selectedPipeline, setSelectedPipeline] = useState<Pipeline | null>(
    null
  );
  const [detailsPipeline, setDetailsPipeline] = useState<Pipeline | null>(
    null
  );
  const [addFormKey, setAddFormKey] = useState(0);
  const [isAddPipelineOpen, setIsAddPipelineOpen] = useState(false);

  const [filters, setFilters] = useState<FilterState>({
    searchTerm: "",
    dateRange: [null, null] as [Date | null, Date | null],
    industryFilter: [],
    countryFilter: [],
    statusFilter: null,
    sortBy: "created_datetime",
    sortOrder: "desc",
    archivedFilter: "no",
  });

  const [isCreatingPipeline, setIsCreatingPipeline] = useState(false);
  const [isUpdatingPipeline, setIsUpdatingPipeline] = useState(false);

  const clearPipelinePageData = () => {
    clearPipelinesData();
    setCurrentPage(1);
    setSelectedItems([]);
    setSelectAll(false);
  };

  useTenantChange({
    onTenantChange: () => {
      setTenantLoading(true);
      clearPipelinePageData();
    },
    refreshData: async () => {
      try {
        await fetchPipelines(1, 12);
      } finally {
        setTenantLoading(false);
      }
    },
    clearData: clearPipelinePageData,
  });

  const filterConfig: FilterConfig = useMemo(
    () => ({
      showSearch: true,
      searchField: "title",
      showDateFilter: true,
      dateField: "created_datetime",
      showIndustryFilter: false,
      showCountryFilter: false,
      showStatusFilter: false, // Keep status filter in dropdown on right side
      pageName: "pipeline" as const,
      defaultSort: {
        field: "created_datetime",
        order: "desc" as const,
      },
    }),
    []
  );

  const handleFilterChange = useCallback(
    (newFilters: FilterState) => {
      const mergedFilters = mergeTableFilterChanges(filters, newFilters);
      setFilters(mergedFilters);
      setCurrentPage(1);
      fetchPipelines(1, 12, mergedFilters);
    },
    [fetchPipelines, filters]
  );

  // Right side filter dropdown change handler
  const handleDropdownFilterChange = useCallback(
    (dropdownFilters: Record<string, any>) => {
      const updatedFilters = {
        ...filters,
        ...dropdownFilters,
      };
      setFilters(updatedFilters);
      setCurrentPage(1);
      fetchPipelines(1, 12, updatedFilters);
    },
    [filters, fetchPipelines]
  );

  const applyListFilters = useCallback(
    (nextFilters: FilterState) => {
      setFilters(nextFilters);
      setCurrentPage(1);
      fetchPipelines(1, 12, nextFilters);
    },
    [fetchPipelines]
  );

  const handleApiFilterChange = useCallback(
    (apiFilters: Record<string, any>) => { },
    []
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchPipelines(page, 12, filters);
  };

  const handleSortChange = useCallback(
    (newSortBy: string, newOrder: "asc" | "desc") => {
      const newFilters = {
        ...filters,
        sortBy: newSortBy,
        sortOrder: newOrder,
      };
      setFilters(newFilters);
    },
    [filters]
  );

  const activityToggle = useSelector(
    (state: any) => state?.activityTogglePopup
  );

  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const defaultColumnVisibility = {
    title: true,
    priority: true,
    realted_lead: true,
    assigned_user: true,
    lifecycle_type_stage: true,
    assigned_team: false,
    primary_contact: false,
    source: false,
    currency_code: false,
    company: true,
  };

  const [columnVisibility, setColumnVisibility] = useState<{
    [key: string]: boolean;
  }>(() => {
    try {
      const saved = localStorage.getItem("pipelines_column_visibility");
      if (saved) {
        const parsed = JSON.parse(saved);
        const visibility: { [key: string]: boolean } = {
          ...defaultColumnVisibility,
        };
        Object.keys(parsed).forEach((key) => {
          if (key in defaultColumnVisibility) {
            visibility[key] = parsed[key] === true;
          }
        });
        const visibleCount = Object.values(visibility).filter(Boolean).length;
        if (visibleCount === 0) {
          const defaultKeys = Object.keys(defaultColumnVisibility);
          for (const key of defaultKeys) {
            if (
              defaultColumnVisibility[
              key as keyof typeof defaultColumnVisibility
              ]
            ) {
              visibility[key] = true;
              break;
            }
          }
        }
        return visibility;
      }
    } catch (error) {
      console.error(
        "Error loading column visibility from localStorage:",
        error
      );
    }
    return defaultColumnVisibility;
  });

  const saveColumnVisibilityToStorage = useCallback(
    (visibility: { [key: string]: boolean }) => {
      try {
        localStorage.setItem(
          "pipelines_column_visibility",
          JSON.stringify(visibility)
        );
      } catch (error) {
        console.error(
          "Error saving column visibility to localStorage:",
          error
        );
      }
    },
    []
  );

  const availableColumns = [
    { key: "title", label: "Title" },
    { key: "company", label: "Company" },
    { key: "priority", label: "Priority" },
    { key: "realted_lead", label: "Related Lead" },
    { key: "assigned_user", label: "Assigned User" },
    { key: "lifecycle_type_stage", label: "Status" },
    { key: "assigned_team", label: "Assigned Team" },
    { key: "primary_contact", label: "Contact Name" },
    { key: "source", label: "Source" },
    { key: "currency_code", label: "Currency" },
  ];

  const [orderedAvailableColumns, setOrderedAvailableColumns] = useState(() => {
    const storageKey = `${window.location.pathname}_column_order`;
    const savedOrder = localStorage.getItem(storageKey);
    if (savedOrder) {
      try {
        const orderKeys = JSON.parse(savedOrder);
        return [...availableColumns].sort((a, b) => {
          const indexA = orderKeys.indexOf(a.key);
          const indexB = orderKeys.indexOf(b.key);
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        });
      } catch (e) {
        return [...availableColumns];
      }
    }
    return [...availableColumns];
  });

  const getSortedAvailableColumns = () => {
    return orderedAvailableColumns;
  };

  const handleColumnVisibilityChange = (
    columnKey: string,
    isVisible: boolean
  ) => {
    const currentVisibleCount =
      Object.values(columnVisibility).filter(Boolean).length;
    if (isVisible && currentVisibleCount >= 6) {
      showErrorAlert(
        "Maximum 6 columns can be enabled at once. Please disable another column first."
      );
      return;
    }
    if (!isVisible && currentVisibleCount <= 1) {
      showErrorAlert("At least one column must remain visible.");
      return;
    }
    const newColumnVisibility = {
      ...columnVisibility,
      [columnKey]: isVisible,
    };
    setColumnVisibility(newColumnVisibility);
    saveColumnVisibilityToStorage(newColumnVisibility);
  };

  const handleResetColumns = () => {
    setColumnVisibility(defaultColumnVisibility);
    saveColumnVisibilityToStorage(defaultColumnVisibility);
  };

  const [currentPage, setCurrentPage] = useState(1);

  const paginatedPipelines = useMemo(
    () =>
      sortRecords(
        Array.isArray(pipelines) ? pipelines : [],
        filters.sortBy,
        filters.sortOrder
      ),
    [pipelines, filters.sortBy, filters.sortOrder]
  );

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      const validIds = paginatedPipelines
        .filter((item) => item.pipeline_id)
        .map((item) => item.pipeline_id as number);
      setSelectedItems(validIds);
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (pipelineId: number) => {
    setSelectedItems((prev) => {
      const newSelected = prev.includes(pipelineId)
        ? prev.filter((id) => id !== pipelineId)
        : [...prev, pipelineId];

      const validCurrentData = paginatedPipelines.filter(
        (item) => item.pipeline_id
      );
      setSelectAll(
        newSelected.length === validCurrentData.length &&
        validCurrentData.length > 0
      );
      return newSelected;
    });
  };

  useEffect(() => {
    if (paginatedPipelines.length > 0) {
      const validCurrentData = paginatedPipelines.filter(
        (item) => item.pipeline_id
      );
      const allCurrentSelected = validCurrentData.every(
        (item) => item.pipeline_id && selectedItems.includes(item.pipeline_id)
      );
      setSelectAll(allCurrentSelected && selectedItems.length > 0);
    } else {
      setSelectAll(false);
    }
  }, [paginatedPipelines, selectedItems]);

  useEffect(() => {
    setSelectedItems([]);
    setSelectAll(false);
  }, [currentPage]);

  useEffect(() => {
    if (!isInitialized) {
      fetchPipelines(1, 12);
    }
  }, [isInitialized, fetchPipelines]);

  useEffect(() => {
    if (isInitialized && backgroundLoading) {
      setTimeout(() => {
        clearBackgroundLoading();
      }, 100);
    }
  }, [isInitialized, backgroundLoading, clearBackgroundLoading]);

  useEffect(() => {
    const editOffcanvas = document.getElementById("offcanvas_edit_pipeline");
    const addOffcanvas = document.getElementById("offcanvas_add_pipeline");

    const handleEditHidden = () => {
      setSelectedPipeline(null);
    };

    const handleAddHidden = () => {
      setAddFormKey((prev) => prev + 1);
      setIsAddPipelineOpen(false);
    };

    if (editOffcanvas) {
      editOffcanvas.addEventListener("hidden.bs.offcanvas", handleEditHidden);
    }

    if (addOffcanvas) {
      addOffcanvas.addEventListener("hidden.bs.offcanvas", handleAddHidden);
    }

    return () => {
      if (editOffcanvas) {
        editOffcanvas.removeEventListener(
          "hidden.bs.offcanvas",
          handleEditHidden
        );
      }
      if (addOffcanvas) {
        addOffcanvas.removeEventListener(
          "hidden.bs.offcanvas",
          handleAddHidden
        );
      }
    };
  }, []);

  useEffect(() => {
    setData(pipelines);
  }, [pipelines]);

  useEffect(() => {
    if (pagination.currentPage && pagination.currentPage !== currentPage) {
      setCurrentPage(pagination.currentPage);
    }
  }, [pagination.currentPage, currentPage]);
  useEffect(() => {
    saveColumnVisibilityToStorage(columnVisibility);
  }, [columnVisibility, saveColumnVisibilityToStorage]);

  const handleCreate = async (pipeline: Partial<PipelineRequest>) => {
    setIsCreatingPipeline(true);
    try {
      const newPipeline = await PipelineService.createPipeline(pipeline);
      addPipeline(newPipeline);
      closeOffcanvas("offcanvas_add_pipeline");

      setTimeout(() => {
        showSuccessAlert(
          `Pipeline "${newPipeline.title}" created successfully!`
        );
      }, 500);
    } catch (err) {
      console.error("Failed to create pipeline:", err);
      showErrorAlert(getApiErrorMessage(err, "Failed to create pipeline. Please try again."));
      throw err;
    } finally {
      setIsCreatingPipeline(false);
    }
  };

  const handleUpdate = async (
    id: number,
    pipeline: Partial<PipelineRequest>
  ) => {
    setIsUpdatingPipeline(true);
    try {
      const updatedPipeline = await PipelineService.updatePipeline(
        id,
        pipeline
      );
      updatePipeline(updatedPipeline);

      closeOffcanvas("offcanvas_edit_pipeline");

      setTimeout(() => {
        showSuccessAlert(
          `Pipeline "${updatedPipeline.title}" updated successfully!`
        );
      }, 500);
    } catch (err) {
      console.error("Failed to update pipeline:", err);
      showErrorAlert(getApiErrorMessage(err, "Failed to update pipeline. Please try again."));
      throw err;
    } finally {
      setIsUpdatingPipeline(false);
    }
  };

  const handleDelete = async (pipeline: Pipeline) => {
    const result = await showConfirmationAlert(
      "Delete Pipeline",
      `Are you sure you want to delete "${pipeline.title}"? This action cannot be undone.`,
      async () => {
        if (pipeline.pipeline_id) {
          await PipelineService.deletePipeline(pipeline.pipeline_id);
        }
      }
    );

    if (result.isConfirmed) {
      if (pipeline.pipeline_id) {
        removePipeline(pipeline.pipeline_id);
      }
      await showSuccessAlert(
        `Pipeline "${pipeline.title}" deleted successfully!`
      );
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;

    const result = await showConfirmationAlert(
      "Delete Selected Records",
      `Are you sure you want to delete ${selectedItems.length} selected record${selectedItems.length > 1 ? "s" : ""}? This action cannot be undone.`,
      async () => {
        setIsBulkDeleting(true);
        try {
          const deletePromises = selectedItems.map((pipelineId) =>
            PipelineService.deletePipeline(pipelineId)
          );
          await Promise.all(deletePromises);
        } finally {
          setIsBulkDeleting(false);
        }
      }
    );

    if (result.isConfirmed && result.value) {
      selectedItems.forEach((pipelineId) => removePipeline(pipelineId));

      setSelectedItems([]);
      setSelectAll(false);

      await showSuccessAlert(
        `${selectedItems.length} pipeline${selectedItems.length > 1 ? "s" : ""
        } deleted successfully!`
      );
    }
  };;

  const closeOffcanvas = (offcanvasId: string) => {
    const modal = document.getElementById(offcanvasId);
    if (modal) {
      const closeButton = modal.querySelector(
        '[data-bs-dismiss="offcanvas"]'
      ) as HTMLElement;
      if (closeButton) {
        closeButton.click();
      }
    }
  };

  const allAvailableColumns = useMemo(
    () => [
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title={`${getLifecycleTypeName("PIPELINE")} Title`}
              field="title"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "title",
        key: "title",
        render: (text: string, record: Pipeline) => {
          return (
            <h2 className="d-flex align-items-center">
              <Link
                to={`/pipelines/${record.pipeline_id}`}
                state={{ pipelineData: record }}
                className="d-flex flex-column"
              >
                {record.title?.length > 35 ? (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id={`tooltip-${record.pipeline_id}`}>{record.title}</Tooltip>}
                  >
                    <span>{`${record.title.substring(0, 35)}...`}</span>
                  </OverlayTrigger>
                ) : (
                  record.title
                )}
                <span className="text-muted small">
                  {record.company?.company_name || "-"}
                </span>
              </Link>
            </h2>
          );
        },
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title="Company"
              field="company"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "company",
        key: "company",
        render: (company: any, record: Pipeline) => (
          <div className={!company?.company_name ? "text-center fw-bold" : ""}>
            {company?.company_name || "-"}
          </div>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title="Priority"
              field="priority"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "priority",
        key: "priority",
        render: (priority: string, record: Pipeline) => {
          const getPriorityIcon = (priority: string) => {
            switch (priority?.toLowerCase()) {
              case "high":
                return (
                  <i className="ti ti-square-rounded-filled me-1 text-danger circle" />
                );
              case "medium":
                return (
                  <i className="ti ti-square-rounded-filled me-1 text-secondary circle" />
                );
              case "low":
                return (
                  <i className="ti ti-square-rounded-filled me-1 text-info circle" />
                );
              default:
                return (
                  <i className="ti ti-square-rounded-filled me-1 text-secondary circle" />
                );
            }
          };

          if (!priority) return <div className="text-center fw-bold">-</div>;
          return (
            <span className="text-default">
              {getPriorityIcon(priority)}
              {priority}
            </span>
          );
        },
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title="Related Lead"
              field="realted_lead"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "realted_lead",
        key: "realted_lead",
        render: (relatedLead: any, record: Pipeline) => (
          <div>
            {relatedLead?.title ? (
              <Link
                to={`/leads/${relatedLead.lead_id}`}
                className="text-decoration-underline"
              >
                {relatedLead.title?.length > 30 ? (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id={`tooltip-lead-${relatedLead.lead_id}`}>{relatedLead.title}</Tooltip>}
                  >
                    <span>{`${relatedLead.title.substring(0, 30)}...`}</span>
                  </OverlayTrigger>
                ) : (
                  relatedLead.title
                )}
              </Link>
            ) : (
              "No Related Lead"
            )}
          </div>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title="Assigned User"
              field="assigned_user"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "assigned_user",
        key: "assigned_user",
        render: (assignedUser: any, record: Pipeline) => (
          <div className="d-flex align-items-center">
            <img
              src={assignedUser?.avatar_url || male}
              alt={assignedUser?.full_name || "Default Avatar"}
              className="me-2 rounded-circle"
              style={{ width: 24, height: 24, objectFit: "cover" }}
            />
            <span>{assignedUser?.full_name || "No User Assigned"}</span>
          </div>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title="Contact Name"
              field="primary_contact"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "primary_contact",
        key: "primary_contact",
        render: (_: any, record: Pipeline) => (
          <div>
            {record.primary_contact?.full_name && (
              <div>{record.primary_contact.full_name}</div>
            )}
            <span className="text-muted small">
              {record.primary_contact?.primary_phone || "-"}
            </span>
          </div>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title={`${getLifecycleTypeName("PIPELINE")} Status`}
              field="lifecycle_type_stage"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "lifecycle_type_stage",
        key: "lifecycle_type_stage",
        render: (lifecycleStage: any, record: Pipeline) => (
          <span
            className={`badge badge-pill badge-status ${getStageColor(
              record.lifecycle_type_stage?.stage_name || "",
              record.lifecycle_type_stage?.lifecycle_type_stage_id,
              (record.lifecycle_type_stage as any)?.stage_order
            )} no-border`}
            style={{ border: "none", borderColor: "transparent" }}
          >
            {lifecycleStage?.stage_name || "-"}
          </span>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title="Assigned Team"
              field="assigned_team"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "assigned_team",
        key: "assigned_team",
        render: (assignedTeam: any, record: Pipeline) => (
          <div>
            {assignedTeam?.team_name || assignedTeam?.name || "No Team Assigned"}
          </div>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title="Source"
              field="source"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "source",
        key: "source",
        render: (text: string, record: Pipeline) => (
          <div className={!(record as any).source ? "text-center fw-bold" : ""}>{(record as any).source || "-"}</div>
        ),
      },
      {
        title: (
          <SortableHeader
            title="Currency"
            field="currency_code"
            currentSort={filters.sortBy}
            currentOrder={filters.sortOrder}
            onSort={handleSortChange}
          />
        ),
        dataIndex: "currency_code",
        key: "currency_code",
        render: (text: string, record: Pipeline) => (
          <div className={!(record as any).currency_code ? "text-center fw-bold" : ""}>{(record as any).currency_code || "-"}</div>
        ),
      },
    ],
    [filters.sortBy, filters.sortOrder, handleSortChange]
  );

  const buildPipelineSections = useCallback(
    (pipeline: Pipeline): EntityDetailSection[] => {
      const assignedTeam = (pipeline as any).assigned_team;
      const relatedLead = (pipeline as any).realted_lead;
      return [
        {
          title: `${getLifecycleTypeName("PIPELINE")} Info`,
          icon: "ti ti-timeline",
          fields: [
            { label: "Title", value: pipeline.title, fullWidth: true },
            {
              label: "Priority",
              value: <PriorityBadge priority={pipeline.priority} />,
            },
            {
              label: "Status",
              value: pipeline.lifecycle_type_stage?.stage_name,
            },
            { label: "Source", value: (pipeline as any).source },
          ],
        },
        {
          title: "Company & Contact",
          icon: "ti ti-building",
          fields: [
            { label: "Company", value: pipeline.company?.company_name },
            {
              label: "Contact Name",
              value: pipeline.primary_contact?.full_name,
            },
            {
              label: "Contact Phone",
              value: pipeline.primary_contact?.primary_phone,
            },
            {
              label: "Related Lead",
              value: relatedLead?.lead_id ? (
                <EntityDetailLink
                  href={`/leads/${relatedLead.lead_id}`}
                  label={relatedLead.title}
                />
              ) : (
                relatedLead?.title
              ),
            },
          ],
        },
        {
          title: "Ownership",
          icon: "ti ti-users",
          fields: [
            {
              label: "Assigned Team",
              value: assignedTeam?.team_name || assignedTeam?.name,
            },
            { label: "Assigned User", value: pipeline.assigned_user?.full_name },
            { label: "Currency", value: (pipeline as any).currency_code },
          ],
        },
        {
          title: "Timestamps",
          icon: "ti ti-clock",
          fields: [
            {
              label: "Created",
              value: (pipeline as any).created_datetime
                ? formatCustomDateTime((pipeline as any).created_datetime)
                : null,
            },
            {
              label: "Updated",
              value: (pipeline as any).updated_datetime
                ? formatCustomDateTime((pipeline as any).updated_datetime)
                : null,
            },
          ],
        },
      ];
    },
    []
  );

  const columns = useMemo(
    () => [
      {
        title: (
          <div className="form-check form-check-md">
            <input
              className="form-check-input"
              type="checkbox"
              id="select-all"
              checked={!!selectAll}
              onChange={handleSelectAll}
            />
          </div>
        ),
        dataIndex: "checkbox",
        render: (text: string, record: Pipeline) => (
          <div className="form-check form-check-md">
            <input
              className="form-check-input"
              type="checkbox"
              id={`select-${record.pipeline_id}`}
              checked={
                record.pipeline_id
                  ? selectedItems.includes(record.pipeline_id)
                  : false
              }
              onChange={() =>
                record.pipeline_id && handleSelectItem(record.pipeline_id)
              }
            />
          </div>
        ),
      },
      ...getSortedAvailableColumns()
        .filter((col) => columnVisibility[col.key])
        .map((col) =>
          allAvailableColumns.find((allCol) => allCol.key === col.key)
        )
        .filter(Boolean),
      {
        title: "Actions",
        dataIndex: "actions",
        render: (text: string, record: Pipeline) => (
          <div className="d-flex align-items-center justify-content-end gap-2">
            <Link
              to="#"
              className="action-icon"
              onClick={(e) => {
                e.preventDefault();
                setDetailsPipeline(record);
              }}
            >
              <i className="ti ti-eye text-blue-light"></i>
            </Link>
            <div className="dropdown table-action">
              <Link
                to="#"
                className="action-icon"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                data-bs-boundary="viewport"
                data-bs-popper-config='{"strategy":"fixed"}'
              >
                <i className="fa fa-ellipsis-v"></i>
              </Link>
              <div className="dropdown-menu dropdown-menu-end">
                <Link
                  className="dropdown-item"
                  to={`/pipelines/${record.pipeline_id}`}
                  state={{ pipelineData: record }}
                >
                  <i className="ti ti-info-circle text-blue-light"></i> View Details
                </Link>
                <Link
                  className="dropdown-item"
                  to="#"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#offcanvas_edit_pipeline"
                  onClick={(e) => {
                    e.preventDefault();
                    flushSync(() => setSelectedPipeline(record));
                  }}
                >
                  <i className="ti ti-edit text-blue"></i> Edit
                </Link>
                <Link
                  className="dropdown-item"
                  to="#"
                  onClick={() => handleDelete(record)}
                >
                  <i className="ti ti-trash text-danger"></i> Delete
                </Link>
              </div>
            </div>
          </div>
        ),
      },
    ],
    [
      columnVisibility,
      selectedItems,
      selectAll,
      handleSelectAll,
      handleSelectItem,
      getSortedAvailableColumns,
      allAvailableColumns,
      handleDelete,
      setSelectedPipeline,
    ]
  );
  return (
    <PipelineFormDataProvider isMainApiCompleted={isInitialized} shouldLoad={isAddPipelineOpen || !!selectedPipeline}>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  {" "}
                  <div className="col-8">
                    <h4 className="page-title">
                      {getLifecycleTypePluralName("PIPELINE")}
                      <span className="count-title">
                        {pagination.totalCount}
                      </span>
                    </h4>
                  </div>
                  <div className="col-4 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
              {/* /Page Header */}

              <div className="card">
                <div className="card-header">
                  <div className="row align-items-center row-gap-2">
                    <div className="col">
                      <ListFilterChips
                        entityType="pipeline"
                        filters={filters}
                        onRemove={(patch) => applyListFilters({ ...filters, ...patch })}
                        onClearAll={() => applyListFilters(getClearedListFilters())}
                        disabled={loading}
                        className="mb-0"
                      />
                    </div>
                    <div className="col-sm-auto">
                      <div
                        className="d-flex align-items-end flex-wrap row-gap-2"
                        style={{
                          alignItems: "flex-end",
                          justifyContent: "flex-end",
                        }}
                      >
                        {selectedItems.length > 0 && (
                          <button
                            type="button"
                            className="btn btn-danger ms-3"
                            onClick={handleBulkDelete}
                            disabled={isBulkDeleting}
                          >
                            <i className="ti ti-trash me-2" />
                            Delete Selected ({selectedItems.length})
                          </button>
                        )}{" "}
                        &nbsp; &nbsp;
                        <ExportComponent entityType="pipeline" filters={filters} />
                        <Link
                          to="#"
                          className="btn btn-primary"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvas_add_pipeline"
                          onClick={() => {
                            setSelectedPipeline(null);
                            setIsAddPipelineOpen(true);
                          }}
                        >
                          <i className="ti ti-square-rounded-plus me-2" />
                          Add {getLifecycleTypeName("PIPELINE")}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-body d-flex align-items-center justify-content-between flex-wrap row-gap-2">
                  <div className="d-flex align-items-center flex-wrap row-gap-2">
                    <TableFilters
                      config={filterConfig}
                      data={pipelines}
                      onFilterChange={handleFilterChange}
                      onApiFilterChange={handleApiFilterChange}
                      sortBy={filters.sort_by}
                      searchTerm={filters.searchTerm}
                      dateRange={filters.dateRange}
                      hideClearButton
                      tenantId={selectedTenantId || undefined}
                      className="mt-4"
                    />
                  </div>
                  <div className="d-flex align-items-center flex-wrap row-gap-2">
                    <SortByDropdown
                      value={filters.sort_by}
                      onChange={(sortBy) =>
                        setFilters((prev) => ({ ...prev, sort_by: sortBy }))
                      }
                    />
                    <span className="mx-2">
                      <LifecycleFilterDropdown
                        entityType="pipeline"
                        onFilterChange={handleDropdownFilterChange}
                        currentFilters={filters}
                        loading={loading}
                      />
                    </span>
                    <ManageColumns
                      availableColumns={availableColumns}
                      columnVisibility={columnVisibility}
                      onColumnVisibilityChange={handleColumnVisibilityChange}
                      onResetColumns={handleResetColumns}
                      onReorderColumns={setOrderedAvailableColumns}
                    />
                    <div className="view-icons ms-3">
                      <Link to="#" className="active">
                        <i className="ti ti-list-tree" />
                      </Link>
                      <Link
                        to="#"
                        onClick={(e) => {
                          e.preventDefault();
                          onViewSwitch?.("kanban");
                        }}
                      >
                        <i className="ti ti-grid-dots" />
                      </Link>
                    </div>
                  </div>
                </div>
                {loading || tenantLoading ? (
                  <div className="card-body">
                    <div
                      className="d-flex flex-column align-items-center justify-content-center"
                      style={{ minHeight: "60vh" }}
                    >
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <div className="mt-2">
                        <small className="text-muted">
                          Loading {getLifecycleTypePluralName("PIPELINE")}...
                        </small>
                      </div>
                    </div>
                  </div>
                ) : error ? (
                  <div className="card-body">
                    <div className="alert alert-danger text-center">
                      <i className="ti ti-alert-circle me-2"></i>
                      {error}
                    </div>
                  </div>
                ) : (
                  <div className="card-body">
                    <div
                      className="table-responsive contact-table-wrapper"
                      style={{ position: "relative" }}
                    >
                      {paginatedPipelines.length === 0 ? (
                        <div className="text-center py-5">
                          <div className="avatar avatar-xl bg-light-gray rounded-circle mb-3 mx-auto">
                            <i className="ti ti-timeline-event-exclamation fs-36 text-gray"></i>
                          </div>
                          <h4 className="text-gray mb-2">
                            No {getLifecycleTypePluralName("PIPELINE")} Found!
                          </h4>
                          <p className="text-muted mb-3">
                            There are no{" "}
                            {getLifecycleTypePluralName("PIPELINE")} to display
                            at the moment.
                          </p>
                          <button
                            className="btn btn-primary"
                            data-bs-toggle="offcanvas"
                            data-bs-target="#offcanvas_add_pipeline"
                            onClick={() => setIsAddPipelineOpen(true)}
                          >
                            <i className="fa fa-plus me-2" />
                            Add Your First {getLifecycleTypeName("PIPELINE")}
                          </button>
                        </div>
                      ) : (
                        <>
                          <Table
                            columns={columns}
                            dataSource={paginatedPipelines}
                            rowKey={(record: Pipeline) =>
                              record.pipeline_id || 0
                            }
                            rowSelection={false}
                            className="table table-striped contact-table"
                            pagination={false}
                          />
                          <TablePagination
                            currentPage={pagination.currentPage || currentPage}
                            pageSize={12}
                            totalItems={pagination.totalCount}
                            filteredItems={pagination.totalCount}
                            isFiltered={
                              filters.searchTerm !== "" ||
                              filters.dateRange[0] !== null ||
                              filters.dateRange[1] !== null
                            }
                            onPageChange={handlePageChange}
                            onPageSizeChange={() => { }}
                            entityName={getLifecycleTypePluralName("PIPELINE")}
                            loading={loading}
                          />
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Add New Pipeline */}
      <div
        className="offcanvas offcanvas-end"
        tabIndex={-1}
        id="offcanvas_add_pipeline"
        style={{ width: "550px" }}
        data-bs-keyboard="false"
      >
        <div className="offcanvas-header border-bottom">
          <h4>Add New {getLifecycleTypeName("PIPELINE")}</h4>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          {isAddPipelineOpen && (
            <PipelineForm
              key={`add-pipeline-form-${addFormKey}`}
              onSubmit={handleCreate}
              onCancel={() => closeOffcanvas("offcanvas_add_pipeline")}
              isSubmitting={isCreatingPipeline}
            />
          )}
        </div>
      </div>
      {/* Edit Pipeline */}
      <div
        className="offcanvas offcanvas-end"
        tabIndex={-1}
        id="offcanvas_edit_pipeline"
        style={{ width: "550px" }}
        data-bs-keyboard="false"
      >
        <div className="offcanvas-header border-bottom">
          <h4>Edit {getLifecycleTypeName("PIPELINE")}</h4>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          {selectedPipeline && (
            <PipelineForm
              pipeline={selectedPipeline}
              onSubmit={(pipelineData) => {
                if (selectedPipeline && selectedPipeline.pipeline_id) {
                  return handleUpdate(
                    selectedPipeline.pipeline_id,
                    pipelineData
                  );
                }
                return Promise.resolve();
              }}
              onCancel={() => closeOffcanvas("offcanvas_edit_pipeline")}
              isMainApiCompleted={isInitialized}
              isSubmitting={isUpdatingPipeline}
            />
          )}
        </div>
      </div>

      {/* Bulk Delete Confirmation Modal */}

      {detailsPipeline && (
        <EntityDetailsModal
          isOpen={!!detailsPipeline}
          onClose={() => setDetailsPipeline(null)}
          title={detailsPipeline.title}
          subtitle={detailsPipeline.company?.company_name || undefined}
          eyebrow={getLifecycleTypeName("PIPELINE")}
          avatarIcon="ti ti-timeline-event-exclamation"
          badge={{
            label:
              detailsPipeline.lifecycle_type_stage?.stage_name || "No Stage",
            tone: "info",
          }}
          sections={buildPipelineSections(detailsPipeline)}
          onEdit={() => {
            const pipeline = detailsPipeline;
            setDetailsPipeline(null);
            flushSync(() => setSelectedPipeline(pipeline));
            openOffcanvas("offcanvas_edit_pipeline");
          }}
        />
      )}

    </PipelineFormDataProvider>
  );
};

export default PipelineList;
